// src/pages/support/SupportTicketsList.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Filter,
  AlertCircle,
  CheckCircle,
  Clock,
  XCircle,
  MessageSquare,
  Calendar,
  TrendingUp,
  AlertTriangle,
  Zap,
  BarChart3,
  Activity,
  Tag as TagIcon,
  Package,
  FileText,
  Users,
  Timer,
  Shield,
  ArrowUpRight,
  ArrowDownRight,
} from 'lucide-react';
import ticketService from '../../services/ticketService';
import useAuth from '../../hooks/useAuth';
import CreateTicketModal from '../../components/support/CreateTicketModal';
import { formatInIST } from '../../utils/dateHelpers';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'OPEN', label: 'Open' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'WAITING_ON_REPORTER', label: 'Waiting on Merchant' },
  { value: 'WAITING_ON_THIRD_PARTY', label: 'Waiting on Third Party' },
  { value: 'RESOLVED', label: 'Resolved' },
  { value: 'CLOSED', label: 'Closed' },
];

const CATEGORY_OPTIONS = [
  { value: '', label: 'All Categories' },
  { value: 'DELIVERY_ISSUE', label: 'Delivery Issue' },
  { value: 'PICKUP_ISSUE', label: 'Pickup Issue' },
  { value: 'DAMAGE', label: 'Damage' },
  { value: 'LOST', label: 'Lost' },
  { value: 'RTO', label: 'RTO' },
  { value: 'COD_ISSUE', label: 'COD Issue' },
  { value: 'OTHER', label: 'Other' },
];

const PRIORITY_OPTIONS = [
  { value: '', label: 'All Priorities' },
  { value: 'P0', label: 'P0 - Critical' },
  { value: 'P1', label: 'P1 - High' },
  { value: 'P2', label: 'P2 - Medium' },
  { value: 'P3', label: 'P3 - Low' },
  { value: 'P4', label: 'P4 - Very Low' },
];

const PRIORITY_COLORS = {
  P0: 'bg-red-100 text-red-800 border-red-300',
  P1: 'bg-orange-100 text-orange-800 border-orange-300',
  P2: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  P3: 'bg-blue-100 text-blue-800 border-blue-300',
  P4: 'bg-gray-100 text-gray-800 border-gray-300',
};

const SEVERITY_COLORS = {
  CRITICAL: 'bg-red-100 text-red-800',
  HIGH: 'bg-orange-100 text-orange-800',
  MEDIUM: 'bg-yellow-100 text-yellow-800',
  LOW: 'bg-blue-100 text-blue-800',
};

const STATUS_COLORS = {
  OPEN: 'bg-blue-100 text-blue-800',
  IN_PROGRESS: 'bg-purple-100 text-purple-800',
  WAITING_ON_REPORTER: 'bg-yellow-100 text-yellow-800',
  WAITING_ON_THIRD_PARTY: 'bg-amber-100 text-amber-800',
  RESOLVED: 'bg-green-100 text-green-800',
  CLOSED: 'bg-gray-100 text-gray-800',
};

const SupportTicketsList = () => {
  const navigate = useNavigate();
  const { getMerchantId } = useAuth();
  const merchantId = getMerchantId();

  const [tickets, setTickets] = useState([]);
  const [dashboardSummary, setDashboardSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    status: '',
    category: '',
    priority: '',
    slaBreached: undefined,
    search: '',
  });

  const [pagination, setPagination] = useState({
    limit: 50,
    offset: 0,
    hasNext: false,
  });

  const fetchTickets = useCallback(async () => {
    if (!merchantId) return;

    setLoading(true);
    setError(null);

    try {
      const params = {
        tenantId: 'wayuvega_b2c',
        requesterId: merchantId,
        ...(filters.status && { status: filters.status }),
        ...(filters.category && { category: filters.category }),
        ...(filters.slaBreached !== undefined && { slaBreached: filters.slaBreached }),
        limit: pagination.limit,
        offset: pagination.offset,
      };

      const response = await ticketService.listTickets(params);
      setTickets(response.data || []);
      setPagination((prev) => ({
        ...prev,
        hasNext: response.page?.has_next || false,
      }));
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [merchantId, filters, pagination.limit, pagination.offset]);

  const fetchDashboardSummary = useCallback(async () => {
    if (!merchantId) return;

    try {
      const summary = await ticketService.getDashboardSummary('wayuvega_b2c', merchantId);
      setDashboardSummary(summary);
    } catch (err) {
      console.error('Failed to load dashboard summary:', err);
    }
  }, [merchantId]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  useEffect(() => {
    fetchDashboardSummary();
    // Refresh dashboard every 5 minutes
    const interval = setInterval(fetchDashboardSummary, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchDashboardSummary]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, offset: 0 }));
  };

  const handleTicketClick = (publicId, e) => {
    // Prevent navigation if user is selecting text
    if (window.getSelection().toString().length > 0) {
      return;
    }
    // Also check if the click target is an input, button, link, or select
    const target = e?.target;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'BUTTON' || target.tagName === 'A' || target.tagName === 'SELECT' || target.closest('button') || target.closest('a') || target.closest('input') || target.closest('select'))) {
      return;
    }
    navigate(`/support/tickets/${publicId}`);
  };

  const handleCreateSuccess = (ticket) => {
    navigate(`/support/tickets/${ticket.public_id}`);
  };

  const isSLABreached = (ticket) => {
    const sla = ticket.sla_info || {};
    return (
      sla.assignment_breached ||
      sla.first_response_breached ||
      sla.resolution_breached
    );
  };

  const getSLABreachType = (ticket) => {
    const sla = ticket.sla_info || {};
    if (sla.resolution_breached) return 'Resolution';
    if (sla.first_response_breached) return 'First Response';
    if (sla.assignment_breached) return 'Assignment';
    return null;
  };

  const formatPriority = (priority) => {
    const p = priority?.final_value || priority?.suggested || 'P3';
    return p.toUpperCase();
  };

  const formatStatus = (status) => {
    return status?.replace(/_/g, ' ') || '';
  };

  const formatSeverity = (severity) => {
    const s = severity?.final_value || severity?.suggested || 'MEDIUM';
    return s;
  };

  // Filter tickets by all filters
  const filteredTickets = useMemo(() => {
    let filtered = tickets;

    // Search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(
        (ticket) =>
          ticket.title?.toLowerCase().includes(searchLower) ||
          ticket.public_id?.toLowerCase().includes(searchLower) ||
          ticket.description?.toLowerCase().includes(searchLower) ||
          ticket.custom_fields?.order_reference_number?.toLowerCase().includes(searchLower) ||
          ticket.custom_fields?.awb_number?.toLowerCase().includes(searchLower)
      );
    }

    // Priority filter (client-side since API might not support it)
    if (filters.priority) {
      filtered = filtered.filter((ticket) => {
        const ticketPriority = formatPriority(ticket.priority);
        return ticketPriority === filters.priority;
      });
    }

    // SLA Breach filter (client-side)
    if (filters.slaBreached !== undefined) {
      filtered = filtered.filter((ticket) => {
        const breached = isSLABreached(ticket);
        return filters.slaBreached ? breached : !breached;
      });
    }

    return filtered;
  }, [tickets, filters]);

  // Calculate active filters count
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.status) count++;
    if (filters.category) count++;
    if (filters.priority) count++;
    if (filters.slaBreached !== undefined) count++;
    if (filters.search) count++;
    return count;
  }, [filters]);

  return (
    <div className="w-full min-h-[calc(100vh-88px)] bg-gradient-to-br from-slate-50 via-gray-50 to-slate-50 overflow-x-hidden">
      <div className="w-full h-full px-2 sm:px-3 lg:px-2 py-2 md:py-2 space-y-2 max-w-full">
        {/* Compact Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl font-bold text-gray-900">Support Tickets</h1>
          </div>
          <button
            onClick={() => setCreateModalOpen(true)}
            className="flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all shadow-sm hover:shadow-md text-xs sm:text-sm whitespace-nowrap"
          >
            <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            <span className="hidden sm:inline">Create Ticket</span>
            <span className="sm:hidden">Create</span>
          </button>
        </div>

        {/* Compact Dashboard Summary */}
        {dashboardSummary && (
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {/* Total Tickets */}
            <div className="bg-white rounded-lg p-2.5 sm:p-3 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <div className="p-1.5 sm:p-2 bg-gradient-to-br from-indigo-100 to-indigo-200 rounded-lg">
                  <MessageSquare className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-indigo-700" />
                </div>
                <TrendingUp className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-500" />
              </div>
              <p className="text-[10px] sm:text-xs font-medium text-gray-600 mb-0.5">Total Tickets</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900">
                {dashboardSummary.overall?.total_tickets || 0}
              </p>
              <div className="mt-1.5 pt-1.5 border-t border-gray-200 flex items-center justify-between text-[10px] sm:text-xs">
                <span className="text-gray-500">This month</span>
                <span className="font-semibold text-gray-700">
                  {dashboardSummary.time_summary?.created_this_month || 0}
                </span>
              </div>
            </div>

            {/* Active Tickets */}
            <div className="bg-white rounded-lg p-2.5 sm:p-3 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <div className="p-1.5 sm:p-2 bg-gradient-to-br from-blue-100 to-blue-200 rounded-lg">
                  <Activity className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-700" />
                </div>
                <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-500" />
              </div>
              <p className="text-[10px] sm:text-xs font-medium text-gray-600 mb-0.5">Active Tickets</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900">
                {(dashboardSummary.overall?.open_tickets || 0) + (dashboardSummary.overall?.in_progress_tickets || 0)}
              </p>
              <div className="mt-1.5 pt-1.5 border-t border-gray-200 flex items-center justify-between text-[10px] sm:text-xs">
                <span className="text-gray-500">Open</span>
                <span className="font-semibold text-blue-600">
                  {dashboardSummary.overall?.open_tickets || 0}
                </span>
              </div>
            </div>

            {/* SLA Breached */}
            <div className="bg-white rounded-lg p-2.5 sm:p-3 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <div className="p-1.5 sm:p-2 bg-gradient-to-br from-red-100 to-red-200 rounded-lg">
                  <AlertTriangle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-700" />
                </div>
                {dashboardSummary.sla_breach_summary?.total_breached > 0 && (
                  <div className="flex items-center gap-1 px-1.5 py-0.5 bg-red-100 rounded-full">
                    <Zap className="w-3 h-3 text-red-600" />
                    <span className="text-[10px] font-bold text-red-600">URGENT</span>
                  </div>
                )}
              </div>
              <p className="text-[10px] sm:text-xs font-medium text-gray-600 mb-0.5">SLA Breached</p>
              <p className="text-xl sm:text-2xl font-bold text-red-600">
                {dashboardSummary.overall?.sla_breached_tickets || 0}
              </p>
              <div className="mt-1.5 pt-1.5 border-t border-gray-200 flex items-center justify-between text-[10px] sm:text-xs">
                <span className="text-gray-500">On Track</span>
                <span className="font-semibold text-green-600">
                  {dashboardSummary.sla_breach_summary?.on_track || 0}
                </span>
              </div>
            </div>

            {/* Resolved */}
            <div className="bg-white rounded-lg p-2.5 sm:p-3 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-2">
                <div className="p-1.5 sm:p-2 bg-gradient-to-br from-green-100 to-green-200 rounded-lg">
                  <CheckCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-700" />
                </div>
                <BarChart3 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-500" />
              </div>
              <p className="text-[10px] sm:text-xs font-medium text-gray-600 mb-0.5">Resolved</p>
              <p className="text-xl sm:text-2xl font-bold text-green-600">
                {dashboardSummary.overall?.resolved_tickets || 0}
              </p>
              <div className="mt-1.5 pt-1.5 border-t border-gray-200 flex items-center justify-between text-[10px] sm:text-xs">
                <span className="text-gray-500">Avg Resolution</span>
                <span className="font-semibold text-gray-700 text-[10px]">
                  {dashboardSummary.time_summary?.average_resolution_time_hours
                    ? `${dashboardSummary.time_summary.average_resolution_time_hours.toFixed(1)}h`
                    : 'N/A'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Additional Stats Row - Compact */}
        {dashboardSummary && (
          <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-6 gap-1.5 sm:gap-2">
            <div className="bg-white rounded-lg p-2 shadow-sm border border-gray-200">
              <p className="text-[10px] text-gray-500 mb-0.5">Unassigned</p>
              <p className="text-base sm:text-lg font-bold text-gray-900">
                {dashboardSummary.overall?.unassigned_tickets || 0}
              </p>
            </div>
            <div className="bg-white rounded-lg p-2 shadow-sm border border-gray-200">
              <p className="text-[10px] text-gray-500 mb-0.5">Awaiting Response</p>
              <p className="text-base sm:text-lg font-bold text-orange-600">
                {dashboardSummary.overall?.awaiting_merchant_response || 0}
              </p>
            </div>
            <div className="bg-white rounded-lg p-2 shadow-sm border border-gray-200">
              <p className="text-[10px] text-gray-500 mb-0.5">Created Today</p>
              <p className="text-base sm:text-lg font-bold text-blue-600">
                {dashboardSummary.time_summary?.created_today || 0}
              </p>
            </div>
            <div className="bg-white rounded-lg p-2 shadow-sm border border-gray-200">
              <p className="text-[10px] text-gray-500 mb-0.5">This Week</p>
              <p className="text-base sm:text-lg font-bold text-gray-900">
                {dashboardSummary.time_summary?.created_this_week || 0}
              </p>
            </div>
            <div className="bg-white rounded-lg p-2 shadow-sm border border-gray-200">
              <p className="text-[10px] text-gray-500 mb-0.5">Avg Response</p>
              <p className="text-base sm:text-lg font-bold text-purple-600">
                {dashboardSummary.time_summary?.average_first_response_time_hours
                  ? `${dashboardSummary.time_summary.average_first_response_time_hours.toFixed(1)}h`
                  : 'N/A'}
              </p>
            </div>
            <div className="bg-white rounded-lg p-2 shadow-sm border border-gray-200">
              <p className="text-[10px] text-gray-500 mb-0.5">Closed</p>
              <p className="text-base sm:text-lg font-bold text-gray-600">
                {dashboardSummary.overall?.closed_tickets || 0}
              </p>
            </div>
          </div>
        )}

        {/* Compact Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-2 border-b border-gray-200 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <Filter className="w-4 h-4 text-gray-600 flex-shrink-0" />
              <h3 className="font-semibold text-gray-900 text-sm">Filters</h3>
              {activeFiltersCount > 0 && (
                <span className="px-1.5 py-0.5 bg-indigo-100 text-indigo-800 text-[10px] font-bold rounded-full whitespace-nowrap">
                  {activeFiltersCount} active
                </span>
              )}
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="text-xs text-indigo-600 hover:text-indigo-700 font-medium whitespace-nowrap flex-shrink-0"
            >
              {showFilters ? 'Hide' : 'Show'} Filters
            </button>
          </div>

          {showFilters && (
            <div className="p-2 sm:p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
              {/* Search */}
              <div className="relative lg:col-span-2">
                <Search className="absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by title, ID, order, AWB..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-2.5 border-2 border-gray-300 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-sm sm:text-base"
                />
              </div>

              {/* Status Filter */}
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="px-3 sm:px-4 py-2 sm:py-2.5 border-2 border-gray-300 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white font-medium text-sm sm:text-base"
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>

              {/* Category Filter */}
              <select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                className="px-3 sm:px-4 py-2 sm:py-2.5 border-2 border-gray-300 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white font-medium text-sm sm:text-base"
              >
                {CATEGORY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>

              {/* Priority Filter */}
              <select
                value={filters.priority}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
                className="px-3 sm:px-4 py-2 sm:py-2.5 border-2 border-gray-300 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white font-medium text-sm sm:text-base"
              >
                {PRIORITY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>

              {/* SLA Breach Filter */}
              <select
                value={filters.slaBreached === undefined ? '' : filters.slaBreached ? 'true' : 'false'}
                onChange={(e) => {
                  const value = e.target.value === '' ? undefined : e.target.value === 'true';
                  handleFilterChange('slaBreached', value);
                }}
                className="px-3 sm:px-4 py-2 sm:py-2.5 border-2 border-gray-300 rounded-lg sm:rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white font-medium text-sm sm:text-base"
              >
                <option value="">All Tickets</option>
                <option value="true">SLA Breached</option>
                <option value="false">On Track</option>
              </select>

              {/* Clear Filters */}
              {activeFiltersCount > 0 && (
                <button
                  onClick={() => {
                    setFilters({
                      status: '',
                      category: '',
                      priority: '',
                      slaBreached: undefined,
                      search: '',
                    });
                  }}
                  className="lg:col-span-5 px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg sm:rounded-xl transition-colors"
                >
                  Clear All Filters
                </button>
              )}
            </div>
          )}
        </div>

        {/* Compact Tickets List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-6 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-indigo-600 border-t-transparent"></div>
              <p className="text-gray-600 mt-3 font-medium text-sm">Loading tickets...</p>
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
              <p className="text-red-600 font-semibold text-sm">{error}</p>
            </div>
          ) : filteredTickets.length === 0 ? (
            <div className="p-6 text-center">
              <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 font-semibold text-sm">No tickets found</p>
              <p className="text-gray-500 text-xs mt-1.5">Create your first support ticket to get started</p>
              <button
                onClick={() => setCreateModalOpen(true)}
                className="mt-3 px-4 py-1.5 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 transition-colors text-sm"
              >
                Create Ticket
              </button>
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="block md:hidden divide-y divide-gray-200">
                {filteredTickets.map((ticket) => {
                  const sla = ticket.sla_info || {};
                  const isBreached = isSLABreached(ticket);
                  const priority = formatPriority(ticket.priority);
                  const severity = formatSeverity(ticket.severity);

                  return (
                    <div
                      key={ticket.public_id}
                      onClick={(e) => handleTicketClick(ticket.public_id, e)}
                      className="p-3 sm:p-4 hover:bg-indigo-50 cursor-pointer transition-colors"
                    >
                      <div className="space-y-2">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-gray-900 text-sm truncate">{ticket.title}</p>
                            <p className="text-xs text-gray-500 font-mono mt-1">#{ticket.public_id}</p>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <span
                              className={`inline-flex items-center px-2 py-1 rounded-lg text-[10px] font-bold border ${
                                PRIORITY_COLORS[priority] || PRIORITY_COLORS.P3
                              }`}
                            >
                              {priority}
                            </span>
                            <span
                              className={`inline-flex items-center px-2 py-1 rounded-lg text-[10px] font-medium ${
                                STATUS_COLORS[ticket.status] || 'bg-gray-100 text-gray-800'
                              }`}
                            >
                              {formatStatus(ticket.status)}
                            </span>
                          </div>
                        </div>

                        {ticket.custom_fields && (
                          <div className="flex items-center gap-2 flex-wrap">
                            {ticket.custom_fields.order_reference_number && (
                              <div className="flex items-center gap-1 px-2 py-0.5 bg-blue-50 rounded text-[10px] font-medium text-blue-700">
                                <Package className="w-3 h-3" />
                                {ticket.custom_fields.order_reference_number}
                              </div>
                            )}
                            {ticket.custom_fields.awb_number && (
                              <div className="flex items-center gap-1 px-2 py-0.5 bg-green-50 rounded text-[10px] font-medium text-green-700">
                                <FileText className="w-3 h-3" />
                                {ticket.custom_fields.awb_number}
                              </div>
                            )}
                          </div>
                        )}

                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-gray-500">Category:</span>
                            <span className="ml-1 font-medium text-gray-700">
                              {ticket.category?.replace(/_/g, ' ') || '-'}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">Severity:</span>
                            <span
                              className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                SEVERITY_COLORS[severity] || SEVERITY_COLORS.MEDIUM
                              }`}
                            >
                              {severity}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center justify-between text-xs">
                          <div className="flex items-center gap-2 text-gray-600">
                            <Calendar className="w-3.5 h-3.5" />
                            <span>{ticket.created_at ? formatInIST(ticket.created_at) : '-'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <MessageSquare className="w-3.5 h-3.5 text-gray-400" />
                            <span className="font-semibold">{ticket.counters?.message_count || 0}</span>
                            {ticket.counters?.merchant_unread_count > 0 && (
                              <span className="px-1.5 py-0.5 bg-indigo-100 text-indigo-800 text-[10px] font-bold rounded-full">
                                {ticket.counters.merchant_unread_count} new
                              </span>
                            )}
                          </div>
                        </div>

                        {isBreached && (
                          <div className="flex items-center gap-1.5 px-2 py-1 bg-red-50 rounded text-[10px]">
                            <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
                            <span className="font-bold text-red-600">
                              {getSLABreachType(ticket)} Breached
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full min-w-[800px]">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
                    <tr>
                      <th className="px-2 sm:px-3 py-2 text-left text-[10px] sm:text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Ticket Details
                      </th>
                      <th className="px-2 sm:px-3 py-2 text-left text-[10px] sm:text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Status & Priority
                      </th>
                      <th className="px-2 sm:px-3 py-2 text-left text-[10px] sm:text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Category & Severity
                      </th>
                      <th className="px-2 sm:px-3 py-2 text-left text-[10px] sm:text-xs font-bold text-gray-700 uppercase tracking-wider">
                        SLA Status
                      </th>
                      <th className="px-2 sm:px-3 py-2 text-left text-[10px] sm:text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Activity
                      </th>
                      <th className="px-2 sm:px-3 py-2 text-left text-[10px] sm:text-xs font-bold text-gray-700 uppercase tracking-wider">
                        Messages
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {filteredTickets.map((ticket) => {
                      const sla = ticket.sla_info || {};
                      const isBreached = isSLABreached(ticket);
                      const priority = formatPriority(ticket.priority);
                      const severity = formatSeverity(ticket.severity);

                      return (
                        <tr
                          key={ticket.public_id}
                          onClick={(e) => handleTicketClick(ticket.public_id, e)}
                          className="hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 cursor-pointer transition-all group"
                        >
                          <td className="px-2 sm:px-3 py-2 sm:py-2.5">
                            <div className="space-y-2">
                              <div className="flex items-start gap-2 sm:gap-3">
                                <div className="flex-1 min-w-0">
                                  <p className="font-bold text-gray-900 group-hover:text-indigo-700 transition-colors truncate text-sm sm:text-base">
                                    {ticket.title}
                                  </p>
                                  <p className="text-xs sm:text-sm text-gray-500 font-mono mt-1">
                                    #{ticket.public_id}
                                  </p>
                                </div>
                                {ticket.tags && ticket.tags.length > 0 && (
                                  <div className="flex flex-wrap gap-1 flex-shrink-0">
                                    {ticket.tags.slice(0, 2).map((tag, idx) => (
                                      <span
                                        key={idx}
                                        className="px-1.5 sm:px-2 py-0.5 bg-purple-100 text-purple-700 text-[10px] sm:text-xs font-medium rounded-full flex items-center gap-1"
                                      >
                                        <TagIcon className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                                        {tag}
                                      </span>
                                    ))}
                                    {ticket.tags.length > 2 && (
                                      <span className="px-1.5 sm:px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] sm:text-xs font-medium rounded-full">
                                        +{ticket.tags.length - 2}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                              {ticket.custom_fields && (
                                <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                                  {ticket.custom_fields.order_reference_number && (
                                    <div className="flex items-center gap-1 sm:gap-1.5 px-1.5 sm:px-2 py-0.5 sm:py-1 bg-blue-50 rounded text-[10px] sm:text-xs font-medium text-blue-700">
                                      <Package className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-blue-600" />
                                      <span className="truncate max-w-[120px] sm:max-w-none">
                                        Order: {ticket.custom_fields.order_reference_number}
                                      </span>
                                    </div>
                                  )}
                                  {ticket.custom_fields.awb_number && (
                                    <div className="flex items-center gap-1 sm:gap-1.5 px-1.5 sm:px-2 py-0.5 sm:py-1 bg-green-50 rounded text-[10px] sm:text-xs font-medium text-green-700">
                                      <FileText className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-green-600" />
                                      <span className="truncate max-w-[120px] sm:max-w-none">
                                        AWB: {ticket.custom_fields.awb_number}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              )}
                              <p className="text-[10px] sm:text-xs text-gray-500 line-clamp-2">
                                {ticket.description}
                              </p>
                            </div>
                          </td>
                          <td className="px-2 sm:px-3 py-2 sm:py-2.5">
                            <div className="space-y-1.5 sm:space-y-2">
                              <span
                                className={`inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 rounded text-[10px] sm:text-xs font-bold ${
                                  STATUS_COLORS[ticket.status] || 'bg-gray-100 text-gray-800'
                                }`}
                              >
                                {formatStatus(ticket.status)}
                              </span>
                              <div>
                                <span
                                  className={`inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 rounded text-[10px] sm:text-xs font-bold border-2 ${
                                    PRIORITY_COLORS[priority] || PRIORITY_COLORS.P3
                                  }`}
                                >
                                  {priority}
                                  {ticket.priority?.final_value !== ticket.priority?.suggested && (
                                    <ArrowUpRight className="w-2.5 h-2.5 sm:w-3 sm:h-3 ml-0.5 sm:ml-1" />
                                  )}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="px-2 sm:px-3 py-2 sm:py-2.5">
                            <div className="space-y-1.5 sm:space-y-2">
                              <span className="text-xs sm:text-sm font-medium text-gray-700">
                                {ticket.category?.replace(/_/g, ' ') || '-'}
                              </span>
                              {ticket.subcategory && (
                                <p className="text-[10px] sm:text-xs text-gray-500">
                                  {ticket.subcategory.replace(/_/g, ' ')}
                                </p>
                              )}
                              <div>
                                <span
                                  className={`inline-flex items-center px-2 sm:px-2.5 py-0.5 sm:py-1 rounded text-[10px] sm:text-xs font-semibold ${
                                    SEVERITY_COLORS[severity] || SEVERITY_COLORS.MEDIUM
                                  }`}
                                >
                                  {severity}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="px-2 sm:px-3 py-2 sm:py-2.5">
                            {isBreached ? (
                              <div className="space-y-1">
                                <div className="flex items-center gap-1 sm:gap-1.5">
                                  <AlertTriangle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-500" />
                                  <span className="text-[10px] sm:text-xs font-bold text-red-600">
                                    {getSLABreachType(ticket)} Breached
                                  </span>
                                </div>
                                {sla.assignment_breached && (
                                  <p className="text-[10px] sm:text-xs text-red-500">
                                    Assignment: {sla.assignment_breach_minutes}m overdue
                                  </p>
                                )}
                                {sla.first_response_breached && (
                                  <p className="text-[10px] sm:text-xs text-red-500">
                                    First Response: {sla.first_response_breach_minutes}m overdue
                                  </p>
                                )}
                                {sla.resolution_breached && (
                                  <p className="text-[10px] sm:text-xs text-red-500">
                                    Resolution: {sla.resolution_breach_minutes}m overdue
                                  </p>
                                )}
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 sm:gap-1.5">
                                <Shield className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-green-500" />
                                <span className="text-[10px] sm:text-xs font-semibold text-green-600">On Track</span>
                              </div>
                            )}
                          </td>
                          <td className="px-2 sm:px-3 py-2 sm:py-2.5">
                            <div className="space-y-1">
                              <div className="flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs text-gray-600">
                                <Calendar className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                                <span className="font-medium truncate">
                                  {ticket.created_at ? formatInIST(ticket.created_at) : '-'}
                                </span>
                              </div>
                              {ticket.updated_at && ticket.updated_at !== ticket.created_at && (
                                <div className="flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs text-gray-500">
                                  <Clock className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                                  <span className="truncate">Updated {formatInIST(ticket.updated_at)}</span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-2 sm:px-3 py-2 sm:py-2.5">
                            <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                              <div className="flex items-center gap-1.5 sm:gap-2">
                                <MessageSquare className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-400 flex-shrink-0" />
                                <span className="text-xs sm:text-sm font-semibold text-gray-700">
                                  {ticket.counters?.message_count || 0}
                                </span>
                              </div>
                              {ticket.counters?.merchant_unread_count > 0 && (
                                <span className="px-1.5 sm:px-2.5 py-0.5 sm:py-1 bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-[10px] sm:text-xs font-bold rounded-full shadow-md">
                                  {ticket.counters.merchant_unread_count} new
                                </span>
                              )}
                              {ticket.counters?.attachment_count > 0 && (
                                <div className="flex items-center gap-1 text-[10px] sm:text-xs text-gray-500">
                                  <FileText className="w-3 h-3 sm:w-3.5 sm:h-3.5 flex-shrink-0" />
                                  <span>{ticket.counters.attachment_count}</span>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Enhanced Pagination */}
              {filteredTickets.length > 0 && (
                <div className="px-2 sm:px-3 py-2 border-t-2 border-gray-200 bg-gray-50 flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs sm:text-sm font-medium text-gray-700">
                      Showing <span className="font-bold">{pagination.offset + 1}</span> -{' '}
                      <span className="font-bold">{pagination.offset + filteredTickets.length}</span> of{' '}
                      <span className="font-bold">{filteredTickets.length}</span> tickets
                    </span>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3">
                    <button
                      onClick={() =>
                        setPagination((prev) => ({
                          ...prev,
                          offset: Math.max(0, prev.offset - prev.limit),
                        }))
                      }
                      disabled={pagination.offset === 0}
                      className="px-3 sm:px-4 py-1.5 sm:py-2 bg-white border-2 border-gray-300 rounded-lg sm:rounded-xl font-semibold text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 hover:border-indigo-500 transition-all shadow-sm text-xs sm:text-sm"
                    >
                      Previous
                    </button>
                    <span className="text-xs sm:text-sm font-medium text-gray-600">
                      Page {Math.floor(pagination.offset / pagination.limit) + 1}
                    </span>
                    <button
                      onClick={() =>
                        setPagination((prev) => ({
                          ...prev,
                          offset: prev.offset + prev.limit,
                        }))
                      }
                      disabled={!pagination.hasNext}
                      className="px-3 sm:px-4 py-1.5 sm:py-2 bg-white border-2 border-gray-300 rounded-lg sm:rounded-xl font-semibold text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 hover:border-indigo-500 transition-all shadow-sm text-xs sm:text-sm"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Create Ticket Modal */}
      <CreateTicketModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={handleCreateSuccess}
      />
    </div>
  );
};

export default SupportTicketsList;
